import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import SendIcon from '@mui/icons-material/Send';

const navy = '#0F1D2E';
const gold = '#D1BC8A';
const goldDark = '#B8A06E';

export default function NotFound() {
  return (
    <>
      <Helmet>
        <title>Seite nicht gefunden — Süd Ausbau GmbH</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(145deg, ${navy} 0%, #1B2D42 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Blueprint grid */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)`,
          backgroundSize: '28px 28px',
        }} />

        {/* Gold top accent */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, transparent 0%, ${gold} 30%, #E8DEC3 60%, ${gold} 100%)`,
          opacity: 0.9,
        }} />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, textAlign: 'center', py: 8 }}>
          {/* 404 number */}
          <Typography
            sx={{
              fontSize: { xs: '6rem', md: '9rem' },
              fontWeight: 900,
              lineHeight: 1,
              mb: 2,
              background: `linear-gradient(135deg, ${gold} 0%, #E8DEC3 50%, ${goldDark} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            404
          </Typography>

          <Typography
            variant="h4"
            sx={{ color: '#fff', fontWeight: 800, mb: 2, letterSpacing: '-0.02em' }}
          >
            Seite nicht gefunden
          </Typography>

          <Typography
            sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', lineHeight: 1.75, mb: 5, maxWidth: 400, mx: 'auto' }}
          >
            Die gesuchte Seite existiert nicht oder wurde verschoben.
            Kein Problem — wir helfen Ihnen gerne weiter.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/"
              startIcon={<HomeIcon />}
              sx={{
                background: `linear-gradient(135deg, ${gold} 0%, ${goldDark} 100%)`,
                color: navy,
                fontWeight: 800,
                px: 3.5,
                py: 1.5,
                borderRadius: '12px',
                boxShadow: `0 10px 28px rgba(209,188,138,0.32)`,
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 36px rgba(209,188,138,0.42)` },
                transition: 'all 0.3s ease',
              }}
            >
              Zur Startseite
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to="/anfrage"
              endIcon={<SendIcon />}
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 600,
                px: 3.5,
                py: 1.5,
                borderRadius: '12px',
                '&:hover': { borderColor: `rgba(209,188,138,0.5)`, color: gold },
                transition: 'all 0.3s ease',
              }}
            >
              Anfrage senden
            </Button>
          </Stack>

          {/* Quick links */}
          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', mb: 2 }}>
              Beliebte Seiten
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
              {[
                { label: 'Leistungen', to: '/leistungen' },
                { label: 'Referenzen', to: '/referenzen' },
                { label: 'Neubau', to: '/neubau' },
                { label: 'Umbau', to: '/umbau' },
                { label: 'Sanierung', to: '/sanierung' },
              ].map((link) => (
                <Button
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '0.82rem',
                    '&:hover': { color: gold },
                    transition: 'color 0.2s',
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
}
